import { useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mantine/core';
import { faSyncAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../common/ConfirmationModal';
import AddLicenseModal from './AddLicense';
import AuthContext from '../../AuthContext';
import Table from '../common/CommonTable'
const LicenseTable = ({ data, setLicenses, onDelete, setTableInstance, userType, onAttachDevices }) => {
  //console.log(data)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [renewLicense, setRenewLicense] = useState(null);
  const [formData, setFormData] = useState({
    expiry_date: '',
    no_license: '',
  });
  const auth = useContext(AuthContext);
  const [licenseType, setLicenseType] = useState('');
  const { state } = useLocation();

  //console.log(data)

  const handleConfirmDelete = () => {
    if (selectedRow) {
      onDelete(selectedRow.id);
      setConfirmOpen(false);
      setSelectedRow(null);
    }
  };

  const handleRenewLicense = () => {
    if (renewLicense) {
      setLicenses((prev) =>
        prev.map((license) =>
          license.id === renewLicense.id
            ? { ...license, expiry_date: formData.expiry_date, quantity: formData.no_license }
            : license
        )
      );
    }
    closeRenewModal();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRippleEffect = (e) => {
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
    ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
    ripple.classList.add('export-button-ripple');

    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 400);
  };

  const openRenewModal = (license) => {
    setRenewLicense(license);
    setFormData({
      expiry_date: license.expiry_date || '',
      no_license: license.quantity || '',
    });
    setLicenseType(license.license_type || '');
    setRenewModalOpen(true);
  };

  const closeRenewModal = () => {
    setRenewModalOpen(false);
    setRenewLicense(null);
    setFormData({
      expiry_date: '',
      no_license: '',
    });
    setLicenseType(null);
  };

  // Check if license is expired
  const isLicenseExpired = (expiryDate) => {
    if (!expiryDate) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  };


  const columns = [
    {
      accessorKey: 'devices',
      header: 'No. of Devices',
      size: 180,
      minSize: 120,
      Cell: ({ cell }) => cell.getValue() || 'N/A',
      mantineTableBodyCellProps: {
        style: {
          fontFamily: 'Nunito Sans, sans-serif',
          fontWeight: 400,
          color: '#595959',
        },
      },
    },
    {
      accessorKey: 'type',
      header: 'Licence Type',
      size: 180,
      minSize: 120,
      Cell: ({ cell }) => cell.getValue() || 'N/A',
      mantineTableBodyCellProps: {
        style: {
          fontFamily: 'Nunito Sans, sans-serif',
          fontWeight: 400,
          color: '#595959',
        },
      },
    },
    {
      accessorKey: 'devices_list',
      header: 'Devices List',
      size: 180,
      minSize: 120,
      Cell: ({ cell }) => cell.getValue() || 'N/A',
      mantineTableBodyCellProps: {
        style: {
          fontFamily: 'Nunito Sans, sans-serif',
          fontWeight: 400,
          color: '#595959',
        },
      },
    },
    {
      accessorKey: 'expiry',
      header: 'Expiration Date',
      size: 180,
      minSize: 120,
      Cell: ({ cell }) => {
        const date = cell.getValue();
        return date
          ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : 'N/A';
      },
      mantineTableBodyCellProps: {
        style: {
          fontFamily: 'Nunito Sans, sans-serif',
          fontWeight: 400,
          color: '#595959',
        },
      },
    },

    ...(userType === 'superadmin'
      ? [
        {
          accessorKey: 'company_name',
          header: 'Company',
          size: 180,
          minSize: 120,
          Cell: ({ cell }) => cell.getValue() || 'N/A',
          mantineTableBodyCellProps: {
            style: {
              fontFamily: 'Nunito Sans, sans-serif',
              fontWeight: 400,
              color: '#595959',
            },
          },
        },
      ]
      : []),

    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 100,
      minSize: 80,
      Cell: ({ row }) => {
        const license = row.original;
        const handleDelete = () => {
          setSelectedRow(license);
          setConfirmOpen(true);
        };

        const handleRenew = () => {
          openRenewModal(license);
        };

        return (
          <div className="flex justify-start items-center space-x-3" style={{ backgroundColor: 'transparent' }}>

            <div className="flex gap-2 justify-center items-center">
              <Button color="orange" size="sm"
                className="export-button" onClick={() => onAttachDevices(license)}
                disabled={
                  (license.type === 'single_device' && (license.devices_list?.length || 0) >= 1) ||
                  (license.type === 'multi_device' && (license.devices_list?.length || 0) >= (license.devices || 0))
                }
              >Attach Devices</Button>
              <FontAwesomeIcon icon={faTrash} className="text-red-500 cursor-pointer" onClick={() => onDelete(license.id)} />

            </div>
            {!isLicenseExpired(license.expiry_date) && auth.userType === 'superadmin' && (
              <FontAwesomeIcon
                icon={faSyncAlt}
                size="lg"
                className="text-[#FF9F43] cursor-pointer hover:text-[#e68f3b]"
                title="Renew License"
                onClick={handleRenew}
              />
            )}

          </div>
        );
      },
      mantineTableBodyCellProps: {
        style: {
          height: '48px',
          backgroundColor: 'transparent !important',
        },
      },
    },
  ];

  const table = useMantineReactTable({
    columns,
    data: data,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableColumnHiding: true,
    mantineTableContainerProps: {
      style: {
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '29.94vw',         // 400px
        paddingLeft: '1.50vw',        // 20px
        paddingRight: '1.50vw',       // 20px
      },
    },
    mantineTableProps: {
      style: {
        minWidth: 'max-content',
        tableLayout: 'auto',
        fontFamily: 'Nunito Sans, sans-serif',
        backgroundColor: '#ffffff',
        width: '100%',
      },
      className: 'shadow-sm rounded-lg',
    },
    mantineTableHeadCellProps: {
      style: {
        whiteSpace: 'nowrap',
        backgroundColor: '#F1F4F9',
        borderBottom: '0.07vw solid #e5e7eb', // 1px
        padding: '0.90vw',                   // 12px
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: '700',
        fontSize: '1.05vw',                  // 14px
        color: '#202224',
      },
    },
    mantineTableBodyRowProps: {
      style: {
        borderBottom: '0.04vw solid #979797', // 0.5px
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#f5f5f5 !important',
        },
      },
    },
    mantineTableBodyCellProps: {
      style: {
        whiteSpace: 'nowrap',
        padding: '0.90vw',                  // 12px
        height: '3.59vw',                   // 48px
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: '400',
        fontSize: '1.05vw',                 // 14px
        color: '#595959',
        backgroundColor: 'transparent !important',
        '&:hover': {
          backgroundColor: 'transparent !important',
        },
      },
    },
    mantineSelectCheckboxProps: {
      size: 'sm',
      styles: {
        input: {
          '&:not(:checked)': {
            border: '0.07vw solid #202224 !important', // 1px
            backgroundColor: 'transparent !important',
          },
          '&:checked': {
            border: 'none !important',
            backgroundColor: '#FF7A00 !important',
          },
        },
        icon: {
          color: '#ffffff !important',
        },
        wrapper: {
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'transparent !important',
        },
      },
      className: 'custom-checkbox-cell',
    },
    mantineSelectAllCheckboxProps: {
      size: 'sm',
      styles: {
        input: {
          '&:not(:checked)': {
            border: '0.07vw solid #202224 !important', // 1px
          },
          '&:checked': {
            border: 'none !important',
            backgroundColor: '#FF7A00 !important',
          },
        },
        icon: {
          color: '#ffffff !important',
        },
        wrapper: {
          borderBottom: '0.07vw solid #e5e7eb', // 1px
        },
      },
    },
    mantineColumnVisibilityToggleButtonProps: {
      sx: {
        color: '#FF7A00 !important',
        fontFamily: 'Nunito Sans, sans-serif',
        fontSize: '1.05vw', // 14px
        border: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: '#FFF3E5',
        },
        '& svg': {
          color: '#FF7A00 !important',
        },
      },
    },
    mantineTableToolbarProps: {
      sx: {
        '& .mantine-Menu-item': {
          color: '#FF7A00 !important',
          fontFamily: 'Nunito Sans, sans-serif',
          fontSize: '1.05vw', // 14px
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: '#FFF3E5 !important',
          },
        },
        '& .mantine-Menu-item[data-testid="mrt-column-visibility-show-all-button"], & .mantine-Menu-item[data-testid="mrt-column-visibility-hide-all-button"]': {
          color: '#FF7A00 !important',
          '&:hover': {
            backgroundColor: '#FFF3E5 !important',
          },
        },
      },
    },

    meta: {
      setLicenses,
    },
  });

  useEffect(() => {
    if (setTableInstance) {
      setTableInstance(table);
    }
  }, [table, setTableInstance]);

  return (
    <div className="mb-5">
      <style>
        {`
          .custom-checkbox-cell,
          .custom-checkbox-cell * {
            background-color: transparent !important;
          }
          .custom-checkbox-cell:hover,
          .custom-checkbox-cell *:hover {
            background-color: transparent !important;
          }
          .mantine-Menu-item[data-testid="mrt-column-visibility-show-all-button"],
          .mantine-Menu-item[data-testid="mrt-column-visibility-hide-all-button"] {
            color: #FF7A00 !important;
            font-family: 'Nunito Sans, sans-serif' !important;
            font-size: 14px !important;
          }
          .mantine-Menu-item[data-testid="mrt-column-visibility-show-all-button"]:hover,
          .mantine-Menu-item[data-testid="mrt-column-visibility-hide-all-button"]:hover {
            backgroundColor: #FFF3E5 !important;
          }
          .export-button-ripple {
            position: absolute;
            border-radius: 50%;
            background-color: rgba(255, 255, 255, 0.4);
            transform: scale(0);
            animation: ripple 400ms linear;
            pointer-events: none;
          }
          @keyframes ripple {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }
        `}
      </style>
      <Table columns={columns} data={data} />
      <ConfirmationModal
        open={confirmOpen}
        onCancel={() => {
          setConfirmOpen(false);
          setSelectedRow(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete License"
        message={`Are you sure you want to delete the license with type "${selectedRow?.license_type || 'Unknown'}"? This action cannot be undone.`}
      />
      <AddLicenseModal
        open={renewModalOpen}
        onClose={closeRenewModal}
        licenseType={licenseType}
        setLicenseType={setLicenseType}
        formData={formData}
        handleInputChange={handleInputChange}
        handleAddLicense={handleRenewLicense}
        handleRippleEffect={handleRippleEffect}
        isRenewing={true}
      />
    </div>
  );
};

export default LicenseTable;